import type { FC, ReactNode } from 'react';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { Tooltip } from '@interstate/components/Tooltip';
import type { TableColumn } from '@interstate/components/Table';

const POSITIVE_COLOR = '#1e7e34';
const NEGATIVE_COLOR = '#c0392b';

const INDICATOR_WIDTH = 10;
const INDICATOR_HEIGHT = 9;
const TRIANGLE_POINTS = { up: '5,0 10,9 0,9', down: '0,0 10,0 5,9' } as const;

type IndicatorDirection = keyof typeof TRIANGLE_POINTS;

const renderTriangle = (direction: IndicatorDirection): ReactNode => (
    <svg
        width={INDICATOR_WIDTH}
        height={INDICATOR_HEIGHT}
        viewBox={`0 0 ${INDICATOR_WIDTH} ${INDICATOR_HEIGHT}`}
        focusable="false"
        aria-hidden="true"
        style={{ display: 'block' }}
    >
        <polygon points={TRIANGLE_POINTS[direction]} fill="currentColor" />
    </svg>
);

type PaymentCellProps = { amount: number; baseline?: number; label?: string; higherIsBetter?: boolean };

export const PaymentCell: FC<PaymentCellProps> = ({ amount, baseline, label = 'Payment', higherIsBetter = false }) => {
    const hasChanged = baseline != null && amount !== baseline;
    const isHigher = hasChanged && amount > (baseline as number);
    const isPositive = higherIsBetter ? isHigher : !isHigher;
    const direction: IndicatorDirection = isHigher ? 'up' : 'down';
    const key = label.toLowerCase();

    return (
        <span
            data-testid={`program-${key}-cell`}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 700,
                color: '#003468',
                whiteSpace: 'nowrap'
            }}
        >
            {formatDollarsAndCents(amount)}
            {hasChanged && (
                <Tooltip
                    position="top"
                    size="large"
                    toolTipContent={`${label} adjusted from ${formatDollarsAndCents(baseline as number)} to ${formatDollarsAndCents(
                        amount
                    )} after applying incentives`}
                >
                    <span
                        data-testid={`program-${key}-indicator`}
                        data-direction={direction}
                        aria-label={isHigher ? `${label} higher than original` : `${label} lower than original`}
                        style={{
                            color: isPositive ? POSITIVE_COLOR : NEGATIVE_COLOR,
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            cursor: 'help',
                            lineHeight: 1
                        }}
                    >
                        {renderTriangle(direction)}
                    </span>
                </Tooltip>
            )}
        </span>
    );
};

type AmountRow = { key?: string };

export const renderLiveAmountCell = (
    value: ReactNode,
    record: AmountRow | undefined,
    appliedKey: string,
    liveAmount?: number,
    baseline?: number,
    label = 'Payment',
    higherIsBetter = false
): ReactNode => {
    const isAppliedRow = Boolean(appliedKey) && record?.key === appliedKey;
    return isAppliedRow && liveAmount != null ? (
        <PaymentCell amount={liveAmount} baseline={baseline} label={label} higherIsBetter={higherIsBetter} />
    ) : (
        value
    );
};

export const withLiveAmountColumn = (
    columns: TableColumn[],
    dataIndex: string,
    appliedKey: string,
    liveAmount?: number,
    baseline?: number,
    label = 'Payment',
    higherIsBetter = false
): TableColumn[] =>
    columns.map((column) =>
        column.dataIndex === dataIndex
            ? {
                  ...column,
                  render: (value: unknown, record: AmountRow) =>
                      renderLiveAmountCell(value as ReactNode, record, appliedKey, liveAmount, baseline, label, higherIsBetter)
              }
            : column
    );

export const renderProgramPaymentCell = (
    value: ReactNode,
    record: AmountRow | undefined,
    appliedKey: string,
    liveMonthlyPayment?: number,
    baseline?: number
): ReactNode => renderLiveAmountCell(value, record, appliedKey, liveMonthlyPayment, baseline, 'Payment');

export const withLivePaymentColumn = (
    columns: TableColumn[],
    appliedKey: string,
    liveMonthlyPayment?: number,
    baseline?: number
): TableColumn[] => withLiveAmountColumn(columns, 'monthlyPayment', appliedKey, liveMonthlyPayment, baseline, 'Payment');
